import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import ListRounded from "@mui/icons-material/ListRounded";
import BubbleChartRounded from "@mui/icons-material/BubbleChartRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import useDebounce from "hooks/useDebounce";
import ChartMindVisualization from "./ChartMindVisualization";
import AddDiagnosisModal from "./AddDiagnosisModal";
import SelectableCard from "./SelectableCard";
import ClinicalReferenceSources from "./ClinicalReferenceSources";
import {
  ClinicianDiagnosisGate,
  ClinicianDiagnosisDialog,
} from "./ClinicianDiagnosis";
import { isSameCondition } from "utils/conditionMatch";

/**
 * DiagnosisStep - Shows DDX results with selectable diagnoses
 *
 * Props:
 * - transcript: The encounter transcript
 * - ddxData: Differential diagnosis data from LLM
 * - loading: Whether DDX is being generated
 * - error: Error message if generation failed
 * - hasDiagnoses: Whether there are diagnoses to show
 * - retry: Function to retry DDX generation
 * - selectedDiagnoses: Array of selected diagnosis conditions
 * - onSelectionChange: Callback when selection changes
 * - onPrefetch: Callback to prefetch/pregenerate note (called after debounce)
 * - disabledDiagnosisIds: Set of hidden diagnosis IDs (condition names)
 * - onToggleDiagnosisDisabled: Callback to toggle diagnosis disabled state
 * - customDiagnoses: User-added diagnoses (session-owned for persistence)
 * - onAddCustomDiagnosis: Called when user adds a diagnosis from the modal
 * - clinicalReferences: Tag-retrieved reference documents backing the DDX
 * - embeddedDocuments: RAG documents injected into the DDX generation
 * - clinicianImpression: The clinician's own answer, recorded before the differential was
 *   shown. Null means it has not been given yet, which is what gates this step.
 * - onRecordClinicianImpression: Called with { condition, reasoning, undetermined } from the
 *   gate, and from the edit dialog afterwards
 * - multiSelect: Let the clinician choose several final diagnoses instead of one. Off by default,
 *   which is ChartMind's behaviour; ChartMind V2 turns it on.
 * - headerExtra: Optional control shown under the heading and its line, once the differential is
 *   on screen (never on the "your diagnosis first" gate). ChartMind passes none; ChartMind V2
 *   puts its diagnosis-prompt switch here.
 */
const DiagnosisStep = ({
  transcript,
  ddxData,
  loading,
  error,
  hasDiagnoses,
  retry,
  selectedDiagnoses = [],
  onSelectionChange,
  onPrefetch,
  disabledDiagnosisIds = new Set(),
  onToggleDiagnosisDisabled,
  customDiagnoses = [],
  onAddCustomDiagnosis,
  clinicalReferences = [],
  embeddedDocuments = [],
  clinicianImpression = null,
  // Off on the Events note, which goes straight to the differential (as in ChartMind-7).
  requireClinicianImpression = true,
  onRecordClinicianImpression,
  multiSelect = false,
  headerExtra = null,
}) => {
  const theme = useTheme();

  // ddxData is null until a request actually returns, so "no data and no error" means the
  // analysis has not run yet rather than that it found nothing. Distinguishing the two is what
  // keeps the empty state from firing during the pre-request debounce window.
  const analysisPending = !loading && !error && !ddxData;

  // ============================================================================
  // TODO: RE-ENABLE VISUALIZATION AFTER FIXING BUGS
  // Temporarily disabled visualization tab due to bugs - needs fixing before deadline
  // Uncomment below to restore visualization functionality:
  // ============================================================================
  // const [viewMode, setViewMode] = useState(1); // 0 = visualization, 1 = list
  // const visualizationContainerRef = useRef(null);
  // const [vizDimensions, setVizDimensions] = useState({ width: 600, height: 400 });

  const isInitialMount = useRef(true);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editImpressionOpen, setEditImpressionOpen] = useState(false);

  // Debounce the selected diagnosis for prefetch (800ms)
  const debouncedSelection = useDebounce(selectedDiagnoses, 800);

  // Trigger prefetch when debounced selection changes (but not on initial mount)
  useEffect(() => {
    // Skip prefetch on initial mount if there's already a selection
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debouncedSelection.length > 0) {
      onPrefetch?.(debouncedSelection);
    }
  }, [debouncedSelection, onPrefetch]);

  // Radio behaviour by default (one final diagnosis); checkbox behaviour when multiSelect.
  const handleSelect = (diagnosis) => {
    const isCurrentlySelected = isSelected(diagnosis);

    if (isCurrentlySelected) {
      // Deselect if clicking the same one
      deselect(diagnosis);
    } else if (multiSelect) {
      onSelectionChange?.([...selectedDiagnoses, diagnosis]);
    } else {
      // Replace with new selection
      onSelectionChange?.([diagnosis]);
    }
  };

  // Check if a diagnosis is selected.
  //
  // The clinician's own entry matches the ranked card it corresponds to even when the two are
  // not spelled identically. Without that, typing "Influenzaa" selects a card that is then
  // hidden as a duplicate of the model's "Influenza", and the step shows a selection the
  // clinician cannot see.
  const matchesSelection = (d, diagnosis) =>
    d.condition === diagnosis.condition ||
    (d.isClinicianImpression && isSameCondition(d.condition, diagnosis.condition));

  const isSelected = (diagnosis) =>
    selectedDiagnoses.some((d) => matchesSelection(d, diagnosis));

  // With one final diagnosis, deselecting clears the selection. With several, it removes only
  // this one, so hiding a card never takes the clinician's other choices with it.
  const deselect = (diagnosis) => {
    onSelectionChange?.(
      multiSelect
        ? selectedDiagnoses.filter((d) => !matchesSelection(d, diagnosis))
        : [],
    );
  };

  // ============================================================================
  // TODO: RE-ENABLE VISUALIZATION AFTER FIXING BUGS
  // ============================================================================
  // const handleViewModeChange = (event, newValue) => {
  //   setViewMode(newValue);
  // };

  // Add diagnosis modal handlers
  const handleOpenAddModal = () => setAddModalOpen(true);
  const handleCloseAddModal = () => setAddModalOpen(false);

  const handleAddDiagnosis = (diagnosis) => {
    onAddCustomDiagnosis?.(diagnosis);
  };

  // Get all existing condition names for duplicate checking
  const existingConditions = [
    ...(ddxData?.primary_diagnoses || []),
    ...(ddxData?.alternative_diagnoses || []),
    ...customDiagnoses,
  ].map((dx) => dx.condition);

  // The clinician's own answer. It is held as a custom diagnosis (so it saves, hydrates and
  // selects like any other) and flagged, so this step can pull it out of "Added Diagnoses" and
  // give it the top of the page.
  const impressionCondition = clinicianImpression?.condition?.trim() || "";
  const impressionCard = customDiagnoses.find((dx) => dx.isClinicianImpression);
  const otherCustomDiagnoses = customDiagnoses.filter(
    (dx) => !dx.isClinicianImpression,
  );

  // Did the model list the same condition, spelling allowed to differ? If it did, ITS entry is
  // the one shown under "Initial Impression" and it comes out of the ranked list below, so the
  // clinician's diagnosis is in one place rather than two. What the model wrote about it is not
  // dropped on the way up — the card carries both rationales.
  //
  // ⚠️ THIS MAKES NO CLAIM ABOUT A DIAGNOSIS BEING MISSING. See utils/conditionMatch: a wrong
  // answer here moves a card, and that is all it is allowed to decide.
  const isImpression = (dx) =>
    Boolean(impressionCondition && isSameCondition(dx.condition, impressionCondition));
  const impressionInDdx = [
    ...(ddxData?.primary_diagnoses || []),
    ...(ddxData?.alternative_diagnoses || []),
  ].find(isImpression);

  // Whichever object represents the clinician's diagnosis on screen: the model's entry when it
  // listed one, otherwise their own. Null only when they answered "not sure yet".
  const impressionSubject = impressionInDdx || impressionCard || null;

  // They answered "not sure yet" and have not since recorded one. A legacy session with no
  // recorded answer at all is not this state — nothing was ever asked of it.
  const awaitingOwnDiagnosis = Boolean(clinicianImpression) && !impressionSubject;

  const rankedPrimary = (ddxData?.primary_diagnoses || []).filter((dx) => !isImpression(dx));
  const rankedAlternative = (ddxData?.alternative_diagnoses || []).filter(
    (dx) => !isImpression(dx),
  );

  // The clinician's own answer, headed and styled like Primary/Alternative Diagnoses so it reads
  // as one more section of the differential rather than a separate, elevated step. Rendered from
  // one place so the two call sites below (the loading/error/empty fallback, and its normal spot
  // after Red Flags) can never drift apart.
  const renderInitialDiagnosis = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        Initial Impression
      </Typography>

      {!impressionSubject ? (
        <>
          <Typography variant="body2" color="text.secondary">
            Recorded as not sure yet.
          </Typography>
          <Button
            size="small"
            startIcon={<AddRounded />}
            onClick={() => setEditImpressionOpen(true)}
            sx={{ mt: 1 }}
          >
            Add your diagnosis
          </Button>
        </>
      ) : (
        <DiagnosisCard
          diagnosis={impressionSubject}
          clinicianReasoning={clinicianImpression.reasoning}
          selected={isSelected(impressionSubject)}
          onSelect={() => handleSelect(impressionSubject)}
          onEdit={() => setEditImpressionOpen(true)}
          disabled={disabledDiagnosisIds.has(impressionSubject.condition)}
          onToggleDisabled={
            onToggleDiagnosisDisabled
              ? () => {
                  if (
                    !disabledDiagnosisIds.has(impressionSubject.condition) &&
                    isSelected(impressionSubject)
                  ) {
                    deselect(impressionSubject);
                  }
                  onToggleDiagnosisDisabled(impressionSubject.condition);
                }
              : undefined
          }
        />
      )}
    </Box>
  );

  // ============================================================================
  // TODO: RE-ENABLE VISUALIZATION AFTER FIXING BUGS
  // ============================================================================
  // useEffect(() => {
  //   if (viewMode === 0 && visualizationContainerRef.current) {
  //     const observer = new ResizeObserver((entries) => {
  //       if (entries[0]) {
  //         const { width } = entries[0].contentRect;
  //         setVizDimensions({ width, height: 400 });
  //       }
  //     });
  //     observer.observe(visualizationContainerRef.current);
  //     return () => observer.disconnect();
  //   }
  // }, [viewMode]);

  // ============================================================================
  // TODO: RE-ENABLE VISUALIZATION AFTER FIXING BUGS
  // ============================================================================
  // const allDiagnosesForViz = useMemo(() => [
  //   ...(ddxData?.primary_diagnoses || []),
  //   ...(ddxData?.alternative_diagnoses || []),
  //   ...customDiagnoses,
  // ], [ddxData?.primary_diagnoses, ddxData?.alternative_diagnoses, customDiagnoses]);

  // No transcript yet
  if (!transcript) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography
          variant="h4"
          sx={{
            mb: { xs: 1.5, sm: 2 },
          }}
        >
          Diagnosis
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            lineHeight: 1.6,
          }}
        >
          Start recording an encounter first to generate diagnosis suggestions.
        </Typography>
      </Box>
    );
  }

  // ⚠️ NOTHING THE MODEL PRODUCED RENDERS BELOW THIS LINE UNTIL THE CLINICIAN HAS ANSWERED —
  // not the differential, not the red flags, not the error. Showing the list first turns
  // diagnosing into grading, which is the habit this gate exists to break. The generation
  // itself is untouched and already running, so the wait is the clinician's own thinking.
  //
  // An encounter that already has a chosen diagnosis and no recorded answer is a chart saved
  // before this gate existed. Asking its question now would be asking someone what they thought
  // before seeing a list they have already worked from, and answering it would overwrite the
  // diagnosis they settled on. Those sessions are let through.
  if (requireClinicianImpression && !clinicianImpression && selectedDiagnoses.length === 0) {
    return (
      <ClinicianDiagnosisGate
        analysisRunning={loading || analysisPending}
        onSubmit={onRecordClinicianImpression}
      />
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            mb: { xs: 1.5, sm: 2 },
          }}
        >
          Differential Diagnosis
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            lineHeight: 1.6,
          }}
        >
          {multiSelect
            ? "Click to select your final diagnoses. Hide diagnoses that don't fit."
            : "Click to select your final diagnosis. Hide diagnoses that don't fit."}
        </Typography>
        {headerExtra}
      </Box>

      {/* Only rendered here — ahead of everything — while there is no results block to put it
          in (loading, erroring, or empty). Once results are in, it moves to its normal spot
          after Red Flags below, so the diagnosis they committed to is never lost from view. */}
      {clinicianImpression && !(!loading && hasDiagnoses) && renderInitialDiagnosis()}

      {/* Error state */}
      {error && (
        <Alert
          severity="error"
          sx={{ width: "100%", mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => retry(transcript)}
              startIcon={<RefreshRounded />}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Loading state. `analysisPending` covers the gap before the call starts: DDX fires off a
          1.5s debounce on the transcript, so arriving here quickly used to mean no spinner, no
          data, and a dead-end "Try Again" box for something that was about to run on its own. */}
      {(loading || analysisPending) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            py: 4,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Analyzing transcript...
          </Typography>
        </Box>
      )}

      {/* DDX Results */}
      {!loading && hasDiagnoses && (
        <Box sx={{ width: "100%" }}>
          {/* ============================================================================ */}
          {/* TODO: RE-ENABLE VISUALIZATION AFTER FIXING BUGS */}
          {/* Temporarily disabled visualization tab switcher due to bugs */}
          {/* ============================================================================ */}
          {/* <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Tabs
              value={viewMode}
              onChange={handleViewModeChange}
              aria-label="view mode"
              sx={{
                minHeight: 0,
                "& .MuiTab-root": {
                  minHeight: 0,
                  textTransform: "none",
                },
              }}
            >
              <Tab
                icon={<BubbleChartRounded sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Visualization"
              />
              <Tab
                icon={<ListRounded sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="List"
              />
            </Tabs>
          </Box> */}

          {/* ============================================================================ */}
          {/* TODO: RE-ENABLE VISUALIZATION AFTER FIXING BUGS */}
          {/* ============================================================================ */}
          {/* {viewMode === 0 && (
            <Box ref={visualizationContainerRef} sx={{ mb: 3 }}>
              <ChartMindVisualization
                diagnoses={allDiagnosesForViz}
                selectedIds={selectedDiagnoses.map((d) => d.condition)}
                onDiagnosisClick={handleSelect}
                onAddDiagnosis={handleOpenAddModal}
                width={vizDimensions.width}
                height={vizDimensions.height}
              />
            </Box>
          )} */}

          {/* Add Diagnosis - top right like PlanStep.
              Held back while the clinician has no diagnosis of their own on record: two buttons
              a line apart, one saying "Add your diagnosis" and one saying "Add Diagnosis", are
              the same action as far as anyone reading them is concerned. Whatever they would
              type into this modal at that point IS their working diagnosis, so it is theirs
              that gets asked for, and this comes back once it is recorded and there is a real
              difference between the two: one more candidate for the differential. */}
          {!awaitingOwnDiagnosis && (
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              mb={3}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddRounded />}
                onClick={handleOpenAddModal}
              >
                Add Diagnosis
              </Button>
            </Box>
          )}

          {/* Added Diagnoses — below Add button, above AI results. The gate's answer is not in
              here; it has its own section above. */}
          {otherCustomDiagnoses.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Added Diagnoses
              </Typography>
              {otherCustomDiagnoses.map((dx, index) => (
                <DiagnosisCard
                  key={`custom-${index}`}
                  diagnosis={dx}
                  selected={isSelected(dx)}
                  onSelect={() => handleSelect(dx)}
                  disabled={disabledDiagnosisIds.has(dx.condition)}
                  onToggleDisabled={
                    onToggleDiagnosisDisabled
                      ? () => {
                          if (!disabledDiagnosisIds.has(dx.condition) && isSelected(dx)) {
                            deselect(dx);
                          }
                          onToggleDiagnosisDisabled(dx.condition);
                        }
                      : undefined
                  }
                />
              ))}
            </Box>
          )}

          {/* Red Flags: show before differential list so they are not missed */}
          {ddxData.red_flags?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 1.5, color: "error.main" }}
              >
                Red Flags
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {ddxData.red_flags.map((flag, index) => (
                  <Chip
                    key={index}
                    label={flag}
                    color="error"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* The clinician's own answer, placed here (not off on its own above) so it reads as
              part of the differential rather than a separate step. */}
          {clinicianImpression && renderInitialDiagnosis()}

          {/* Primary Diagnoses */}
          {rankedPrimary.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Primary Diagnoses
              </Typography>
              {rankedPrimary.map((dx, index) => (
                <DiagnosisCard
                  key={index}
                  diagnosis={dx}
                  selected={isSelected(dx)}
                  onSelect={() => handleSelect(dx)}
                  disabled={disabledDiagnosisIds.has(dx.condition)}
                  onToggleDisabled={
                    onToggleDiagnosisDisabled
                      ? () => {
                          if (!disabledDiagnosisIds.has(dx.condition) && isSelected(dx)) {
                            deselect(dx);
                          }
                          onToggleDiagnosisDisabled(dx.condition);
                        }
                      : undefined
                  }
                />
              ))}
            </Box>
          )}

          {/* Alternative Diagnoses */}
          {rankedAlternative.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Alternative Diagnoses
              </Typography>
              {rankedAlternative.map((dx, index) => (
                <DiagnosisCard
                  key={index}
                  diagnosis={dx}
                  selected={isSelected(dx)}
                  onSelect={() => handleSelect(dx)}
                  disabled={disabledDiagnosisIds.has(dx.condition)}
                  onToggleDisabled={
                    onToggleDiagnosisDisabled
                      ? () => {
                          if (!disabledDiagnosisIds.has(dx.condition) && isSelected(dx)) {
                            deselect(dx);
                          }
                          onToggleDiagnosisDisabled(dx.condition);
                        }
                      : undefined
                  }
                />
              ))}
            </Box>
          )}

          {/* Reference documents that backed the differential */}
          <ClinicalReferenceSources
            clinicalReferences={clinicalReferences}
            embeddedDocuments={embeddedDocuments}
          />
        </Box>
      )}

      {/* No results after analysis actually ran. Plain centered copy like the other empty states
          here — the dashed box read as an error panel for what is just a thin transcript. */}
      {!loading && !analysisPending && !hasDiagnoses && !error && (
        <Box sx={{ width: "100%", py: 6, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No diagnoses generated. The transcript may need more clinical information.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => retry(transcript)}
            startIcon={<RefreshRounded />}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Box>
      )}

      <ClinicianDiagnosisDialog
        open={editImpressionOpen}
        impression={clinicianImpression}
        onClose={() => setEditImpressionOpen(false)}
        onSubmit={onRecordClinicianImpression}
      />

      {/* Add Diagnosis Modal */}
      <AddDiagnosisModal
        open={addModalOpen}
        onClose={handleCloseAddModal}
        onAdd={handleAddDiagnosis}
        existingConditions={existingConditions}
      />
    </Box>
  );
};

/**
 * Individual diagnosis card - single selection (radio-like behavior)
 */
const DiagnosisCard = ({
  diagnosis,
  selected,
  onSelect,
  disabled,
  onToggleDisabled,
  onEdit,
  clinicianReasoning,
}) => {
  const getLikelihoodColor = (likelihood) => {
    if (typeof likelihood === "string") {
      const lower = likelihood.toLowerCase();
      if (lower.includes("more likely") || lower.includes("likely")) {
        if (lower.includes("unlikely") || lower.includes("less")) {
          return "default";
        }
        return "success";
      }
      if (lower.includes("unlikely") || lower.includes("less likely")) {
        return "default";
      }
    }
    return "default";
  };

  // The gate's answer carries no likelihood — nothing ranked it, and inventing one would put a
  // confidence on the clinician's own words that they never gave.
  const chips = diagnosis.likelihood
    ? [
        {
          label: diagnosis.likelihood,
          color: getLikelihoodColor(diagnosis.likelihood),
          variant: "outlined",
        },
      ]
    : [];

  if (diagnosis.urgent) {
    chips.push({
      label: "Urgent",
      color: "warning",
      variant: "filled",
    });
  }

  if (selected) {
    chips.push({
      label: "Final Diagnosis",
      color: "primary",
      variant: "filled",
    });
  }

  /**
   * ⚠️ TWO RATIONALES, BOTH KEPT, AND NEVER RUN TOGETHER INTO ONE PARAGRAPH.
   *
   * On the clinician's diagnosis these are two different claims by two different authors: what
   * the clinician saw in the room, and why the model ranked the condition where it did. Merging
   * them would let the model's reasoning be read as the clinician's own, which is exactly the
   * confusion this step exists to prevent, and dropping either one loses something the encounter
   * was supposed to capture. So they are labelled and stacked.
   */
  // ⚠️ THE CLINICIAN'S OWN ENTRY HAS NO RATIONALE FROM THE DIFFERENTIAL, whatever its
  // `rationale` field says. The session puts their findings there so their diagnosis selects,
  // saves and hydrates like every other card — so attributing that field to the model prints
  // the clinician's own sentence back at them under someone else's name, and invents an
  // agreement that never happened.
  const modelRationale = diagnosis.isClinicianImpression ? "" : diagnosis.rationale;
  const bothRationales = Boolean(clinicianReasoning && modelRationale);

  const attributed = (label, text) => (
    <Box sx={{ mt: 0.5 }}>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", fontWeight: 700, display: "block" }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {text}
      </Typography>
    </Box>
  );

  return (
    <SelectableCard
      title={diagnosis.condition}
      // One rationale needs no byline: there is nobody to confuse it with.
      rationale={bothRationales ? undefined : clinicianReasoning || modelRationale}
      additionalContent={
        bothRationales ? (
          <>
            {attributed("Your findings", clinicianReasoning)}
            {attributed("From the differential", modelRationale)}
          </>
        ) : undefined
      }
      selectionVariant="radio"
      selected={selected}
      onClick={onSelect}
      chips={chips}
      disabled={disabled}
      onEdit={onEdit}
      editTooltip="Edit your diagnosis"
      onToggleDisabled={onToggleDisabled}
    />
  );
};

export default DiagnosisStep;
